/**
 * Permission Recalculation Button Component
 *
 * Triggers permission recalculation for individual users or batch operations
 */

import React, { useState } from 'react';
import {
  Box,
  ButtonGroup,
  Chip,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  TextField,
  Divider
} from '@mui/material';
import {
  Refresh,
  Calculate,
  Warning,
  Info,
  CheckCircle,
  Schedule,
  Person,
  Group,
  AccessTime
} from '@mui/icons-material';

import AdminButton from '../../../../../../components/common/AdminButton';

export default function PermissionRecalculationButton({
  userId,
  userName,
  isStale,
  lastCalculated,
  onRecalculate,
  isLoading,
  mode = 'single' // 'single', 'batch', 'all'
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recalculateMode, setRecalculateMode] = useState(mode);
  const [includeRelated, setIncludeRelated] = useState(false);
  const [forceRecalculate, setForceRecalculate] = useState(false);
  const [batchUserIds, setBatchUserIds] = useState('');

  const handleRecalculate = () => {
    if (onRecalculate) {
      const options = {
        mode: recalculateMode,
        userId: userId,
        includeRelated,
        forceRecalculate,
        timestamp: new Date().toISOString()
      };

      if (recalculateMode === 'batch' && batchUserIds) {
        options.userIds = batchUserIds
          .split(/[\n,]/)
          .map(id => id.trim())
          .filter(Boolean);
      }

      onRecalculate(options);
      setDialogOpen(false);
      setBatchUserIds('');
    }
  };

  const getTimeSinceCalculation = () => {
    if (!lastCalculated) return 'Never calculated';

    const now = new Date();
    const last = new Date(lastCalculated);
    const diffMs = now - last;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  const getButtonColor = () => {
    if (isStale) return 'warning';
    if (!lastCalculated) return 'error';
    return 'primary';
  };

  const getUserCount = () => {
    if (recalculateMode === 'single') return 1;
    if (recalculateMode === 'all') return 'All';
    if (recalculateMode === 'batch' && batchUserIds) {
      const count = batchUserIds
        .split(/[\n,]/)
        .map(id => id.trim())
        .filter(Boolean).length;
      return count;
    }
    return 0;
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        {isStale && (
          <Chip
            icon={<Warning />}
            label="Stale"
            size="small"
            color="warning"
            variant="outlined"
          />
        )}

        <AdminButton
          startIcon={<Calculate />}
          onClick={() => setDialogOpen(true)}
          variant={isStale ? 'contained' : 'outlined'}
          size="small"
          disabled={isLoading}
        >
          Recalculate
        </AdminButton>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <Calculate color="primary" />
            <Typography variant="h6">Recalculate Permissions</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {/* Status Info */}
          {userId && (
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Status
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" spacing={2}>
                  <Person fontSize="small" color="action" />
                  <Typography variant="body2">
                    {userName || userId}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <AccessTime fontSize="small" color="action" />
                  <Typography variant="body2">
                    Last calculated: {getTimeSinceCalculation()}
                  </Typography>
                </Stack>
                {isStale && (
                  <Stack direction="row" spacing={2}>
                    <Warning fontSize="small" color="warning" />
                    <Typography variant="body2" color="warning.main">
                      Permissions are stale and need recalculation
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Recalculation Mode */}
          <Typography variant="subtitle2" gutterBottom>
            Recalculation Mode
          </Typography>
          <RadioGroup
            value={recalculateMode}
            onChange={(e) => setRecalculateMode(e.target.value)}
          >
            <FormControlLabel
              value="single"
              control={<Radio />}
              label={
                <Stack>
                  <Typography variant="body2">Single User</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Recalculate permissions for {userName || userId || 'current user'} only
                  </Typography>
                </Stack>
              }
              disabled={!userId}
            />
            <FormControlLabel
              value="batch"
              control={<Radio />}
              label={
                <Stack>
                  <Typography variant="body2">Batch Users</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Recalculate for specific users
                  </Typography>
                </Stack>
              }
            />
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={
                <Stack>
                  <Typography variant="body2">All Users</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Recalculate permissions for all users in the system
                  </Typography>
                </Stack>
              }
            />
          </RadioGroup>

          {/* Batch User Input */}
          {recalculateMode === 'batch' && (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="User IDs"
              value={batchUserIds}
              onChange={(e) => setBatchUserIds(e.target.value)}
              placeholder="Enter user IDs, one per line or comma-separated"
              helperText={`${getUserCount()} user(s) selected`}
              sx={{ mt: 2 }}
            />
          )}

          <Divider sx={{ my: 2 }} />

          {/* Options */}
          <Typography variant="subtitle2" gutterBottom>
            Options
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeRelated}
                  onChange={(e) => setIncludeRelated(e.target.checked)}
                />
              }
              label={
                <Stack>
                  <Typography variant="body2">Include Related Users</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Also recalculate users in the same group/role
                  </Typography>
                </Stack>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={forceRecalculate}
                  onChange={(e) => setForceRecalculate(e.target.checked)}
                />
              }
              label={
                <Stack>
                  <Typography variant="body2">Force Recalculation</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Recalculate even if permissions are not stale
                  </Typography>
                </Stack>
              }
            />
          </Stack>

          {/* Warning for batch/all operations */}
          {(recalculateMode === 'batch' || recalculateMode === 'all') && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Batch recalculation may take several minutes depending on the number of users.
                {recalculateMode === 'all' && ' This will affect all users in the system.'}
              </Typography>
            </Alert>
          )}

          {/* Info about recalculation */}
          <Alert severity="info" icon={<Info />} sx={{ mt: 2 }}>
            <Typography variant="body2">
              Recalculation will:
            </Typography>
            <List dense sx={{ mt: 1 }}>
              <ListItem>• Fetch all applicable grants for the user(s)</ListItem>
              <ListItem>• Apply priority-based permission resolution</ListItem>
              <ListItem>• Update navigation and data scope permissions</ListItem>
              <ListItem>• Cache the results for improved performance</ListItem>
            </List>
          </Alert>

          {isLoading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Recalculating permissions...
              </Typography>
              <LinearProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <AdminButton onClick={() => setDialogOpen(false)} disabled={isLoading} variant="text">
            Cancel
          </AdminButton>
          <AdminButton
            onClick={handleRecalculate}
            variant="contained"
            disabled={isLoading || (recalculateMode === 'batch' && getUserCount() === 0)}
            startIcon={<Calculate />}
          >
            Recalculate {getUserCount()} User{getUserCount() !== 1 ? 's' : ''}
          </AdminButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}